// The Authority panel: everything one team member (or several at once, or a
// saved template) may see and do. Opens from the shield icon on the Team page.
import React, { useEffect, useMemo, useState } from 'react';
import { ShieldCheck, X, Search, Mail, Globe, Layers, Rows3, Download, Briefcase, BookmarkPlus } from 'lucide-react';
import { teamApi, describePermissions } from '../../services/account';
import { Toggle, Segmented, Button, Notice, inputClass, Badge } from './ui';

const KIND_LABEL = { projects: 'Projects', competitors: 'Competitors', gaps: 'Affiliate gaps' };
const KIND_HINT = {
  projects: 'Your own brands on the dashboard',
  competitors: 'Brands tracked in Competitor Spy',
  gaps: 'Competitor-vs-you comparisons in Affiliate Gap',
};

const levelOptions = (levels) => {
  if (levels.length === 2) {
    return [{ value: 'none', label: 'Off', tone: 'off' }, { value: 'view', label: 'On' }];
  }
  return [
    { value: 'none', label: 'None', tone: 'off' },
    { value: 'view', label: 'View' },
    { value: 'edit', label: 'Edit' },
  ];
};

const clone = (value) => JSON.parse(JSON.stringify(value));

const Section = ({ icon: Icon, title, hint, children }) => (
  <section className="px-5 py-4 border-b border-gray-100 dark:border-slate-800">
    <div className="flex items-center gap-2 mb-3">
      {Icon && <Icon className="w-4 h-4 text-blue-600 dark:text-blue-400" />}
      <h3 className="text-sm font-semibold text-gray-900 dark:text-slate-100">{title}</h3>
    </div>
    {hint && <p className="-mt-2 mb-3 text-xs text-gray-500 dark:text-slate-400">{hint}</p>}
    {children}
  </section>
);

/** Pick some of a long list (brands, countries) with a search box. */
const PickList = ({ items, selected, onToggle, onSetAll, empty, renderLabel, height = 'max-h-48' }) => {
  const [q, setQ] = useState('');
  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    return s ? items.filter((it) => renderLabel(it).toLowerCase().includes(s)) : items;
  }, [items, q, renderLabel]);
  if (!items.length) return <p className="text-xs text-gray-500 dark:text-slate-400">{empty}</p>;
  return (
    <div className="rounded-lg border border-gray-200 dark:border-slate-700">
      <div className="flex items-center gap-2 px-2 py-1.5 border-b border-gray-100 dark:border-slate-800">
        <Search className="w-3.5 h-3.5 text-gray-400" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search"
          className="flex-1 bg-transparent text-xs text-gray-800 dark:text-slate-200 focus:outline-none"
        />
        <button type="button" className="text-[11px] text-blue-600 dark:text-blue-400 hover:underline" onClick={() => onSetAll(shown, true)}>All</button>
        <button type="button" className="text-[11px] text-gray-500 dark:text-slate-400 hover:underline" onClick={() => onSetAll(shown, false)}>None</button>
      </div>
      <div className={`${height} overflow-y-auto py-1`}>
        {shown.map((it) => (
          <label key={it.id ?? it} className="flex items-center gap-2 px-2.5 py-1 text-xs text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-800 cursor-pointer">
            <input type="checkbox" className="rounded border-gray-300" checked={selected.has(it.id ?? it)} onChange={() => onToggle(it.id ?? it)} />
            <span className="truncate">{renderLabel(it)}</span>
          </label>
        ))}
        {!shown.length && <p className="px-2.5 py-2 text-xs text-gray-400">No matches</p>}
      </div>
    </div>
  );
};

/**
 * mode: 'member' | 'bulk' | 'template'
 * member: { id, name, email, role } (member mode)
 * memberIds: number[] (bulk mode)
 * template: saved template row, or null for a new one (template mode)
 */
const AuthorityDrawer = ({ open, mode, member, memberIds = [], template, options, onClose, onSaved }) => {
  const [permissions, setPermissions] = useState(null);
  const [brandIds, setBrandIds] = useState(new Set());
  const [mailboxIds, setMailboxIds] = useState(new Set());
  const [templateId, setTemplateId] = useState(null);
  const [startFrom, setStartFrom] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [applyToMembers, setApplyToMembers] = useState(true);
  const [saveAsName, setSaveAsName] = useState(null); // null = closed; string = editing
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const features = options?.features || [];
  const brands = options?.brands || { projects: [], competitors: [], gaps: [] };

  useEffect(() => {
    if (!open || !options) return;
    setError('');
    setSaveAsName(null);
    setStartFrom('');
    if (mode === 'member' && member) {
      setLoading(true);
      teamApi.permissions(member.id)
        .then((data) => {
          setPermissions(data.permissions);
          setBrandIds(new Set(data.brandIds));
          setMailboxIds(new Set(data.mailboxIds));
          setTemplateId(data.templateId);
        })
        .catch((e) => setError(e.message))
        .finally(() => setLoading(false));
    } else if (mode === 'template' && template) {
      setPermissions(clone(template.permissions));
      setBrandIds(new Set(template.brand_ids || []));
      setMailboxIds(new Set());
      setName(template.name);
      setDescription(template.description || '');
      setApplyToMembers(true);
      setTemplateId(template.id);
    } else {
      setPermissions(clone(options.defaultPermissions));
      setBrandIds(new Set());
      setMailboxIds(new Set());
      setTemplateId(null);
      setName('');
      setDescription('');
    }
  }, [open, mode, member, template, options]);

  if (!open) return null;

  const isCoAdmin = mode === 'member' && member?.role === 'co_admin';

  const setFeature = (key, level) => setPermissions((p) => ({ ...p, features: { ...p.features, [key]: level } }));
  const setBrand = (kind, patch) => setPermissions((p) => ({ ...p, brands: { ...p.brands, [kind]: { ...p.brands[kind], ...patch } } }));
  const toggleIn = (setter) => (id) => setter((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const setAllIn = (setter) => (items, value) => setter((prev) => {
    const next = new Set(prev);
    items.forEach((it) => (value ? next.add(it.id ?? it) : next.delete(it.id ?? it)));
    return next;
  });

  const applyStart = (value) => {
    setStartFrom(value);
    if (!value) return;
    const [kind, key] = value.split(':');
    if (kind === 'preset') {
      const preset = options.presets.find((p) => p.key === key);
      if (preset) { setPermissions(clone(preset.permissions)); setTemplateId(null); }
    } else {
      const t = options.templates.find((x) => String(x.id) === key);
      if (t) {
        setPermissions(clone(t.permissions));
        setBrandIds(new Set(t.brand_ids || []));
        setTemplateId(t.id);
      }
    }
  };

  const brandCounts = Object.fromEntries(Object.keys(KIND_LABEL).map((kind) => [
    kind, (brands[kind] || []).filter((b) => brandIds.has(b.id)).length,
  ]));

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const body = { permissions, brandIds: [...brandIds], mailboxIds: [...mailboxIds] };
      if (mode === 'member') {
        await teamApi.savePermissions(member.id, { ...body, templateId });
        onSaved(`Saved ${member.name || member.email}'s access`);
      } else if (mode === 'bulk') {
        const res = await teamApi.bulk({ ...body, memberIds, templateId: undefined });
        onSaved(res.message || 'Saved');
      } else {
        if (!name.trim()) throw new Error('Give the template a name');
        const t = { name: name.trim(), description, permissions, brandIds: [...brandIds] };
        const res = template
          ? await teamApi.updateTemplate(template.id, { ...t, applyToMembers })
          : await teamApi.createTemplate(t);
        onSaved(res.message || 'Template saved');
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const saveAsTemplate = async () => {
    if (!saveAsName || !saveAsName.trim()) return;
    setSaving(true);
    setError('');
    try {
      const res = await teamApi.createTemplate({ name: saveAsName.trim(), permissions, brandIds: [...brandIds] });
      setTemplateId(res.data?.id || null);
      setSaveAsName(null);
      onSaved(`Template "${saveAsName.trim()}" saved — pick it next time you add someone`, { keepOpen: true });
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const title = mode === 'member'
    ? `Authority — ${member?.name || member?.email}`
    : mode === 'bulk'
      ? `Authority for ${memberIds.length} ${memberIds.length === 1 ? 'person' : 'people'}`
      : template ? `Template — ${template.name}` : 'New template';

  const groups = features.reduce((acc, f) => {
    (acc[f.group] = acc[f.group] || []).push(f);
    return acc;
  }, {});

  return (
    <div className="fixed inset-0 z-[60] flex justify-end">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-2xl h-full flex flex-col bg-white dark:bg-slate-900 border-l border-gray-200 dark:border-slate-700 shadow-2xl">
        {/* header */}
        <div className="px-5 py-4 border-b border-gray-200 dark:border-slate-700">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-blue-600 dark:text-blue-400 shrink-0" />
                <h2 className="text-base font-semibold text-gray-900 dark:text-slate-100 truncate">{title}</h2>
              </div>
              {mode === 'member' && <p className="mt-0.5 ml-7 text-xs text-gray-500 dark:text-slate-400">{member?.email}</p>}
              {mode === 'bulk' && (
                <p className="mt-0.5 ml-7 text-xs text-gray-500 dark:text-slate-400">
                  Everything below replaces what these people can do now.
                </p>
              )}
            </div>
            <button type="button" onClick={onClose} className="p-1 rounded-md text-gray-400 hover:text-gray-700 dark:hover:text-slate-200" aria-label="Close">
              <X className="w-5 h-5" />
            </button>
          </div>

          {!isCoAdmin && permissions && mode !== 'template' && (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span className="text-xs text-gray-600 dark:text-slate-300">Start from</span>
              <select value={startFrom} onChange={(e) => applyStart(e.target.value)} className="rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-1 text-xs text-gray-800 dark:text-slate-100">
                <option value="">— keep current —</option>
                <optgroup label="Ready-made">
                  {(options.presets || []).map((p) => <option key={p.key} value={`preset:${p.key}`}>{p.name}</option>)}
                </optgroup>
                {(options.templates || []).length > 0 && (
                  <optgroup label="Your templates">
                    {options.templates.map((t) => <option key={t.id} value={`template:${t.id}`}>{t.name}</option>)}
                  </optgroup>
                )}
              </select>
            </div>
          )}
          {permissions && !isCoAdmin && (
            <p className="mt-2 text-xs leading-relaxed text-gray-600 dark:text-slate-300 bg-gray-50 dark:bg-slate-800/60 rounded-lg px-3 py-2">
              <span className="font-medium">In short:</span> {describePermissions(permissions, features, brandCounts)}
            </p>
          )}
        </div>

        {/* body */}
        <div className="flex-1 overflow-y-auto">
          {loading && <p className="p-5 text-sm text-gray-500">Loading…</p>}
          {error && <div className="p-5 pb-0"><Notice>{error}</Notice></div>}

          {isCoAdmin && (
            <div className="p-5">
              <Notice tone="blue">
                Co-admins can use every feature, see every brand and manage the team. Only the account
                owner can change billing. To restrict this person, change their role to Member on the Team page.
              </Notice>
            </div>
          )}

          {!loading && permissions && !isCoAdmin && (
            <>
              {mode === 'template' && (
                <Section title="Template">
                  <div className="space-y-2">
                    <input className={inputClass} value={name} onChange={(e) => setName(e.target.value)} placeholder="Template name, e.g. UK Casino Analyst" maxLength={80} />
                    <input className={inputClass} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="What it's for (optional)" maxLength={500} />
                    {template && (
                      <label className="flex items-center gap-2 text-xs text-gray-700 dark:text-slate-300">
                        <input type="checkbox" checked={applyToMembers} onChange={(e) => setApplyToMembers(e.target.checked)} />
                        Also update everyone using this template ({template.member_count || 0})
                      </label>
                    )}
                  </div>
                </Section>
              )}

              <Section icon={Layers} title="Features" hint="Dashboard is always on. Hidden features disappear from their menu.">
                <div className="space-y-4">
                  {Object.entries(groups).map(([group, items]) => (
                    <div key={group}>
                      <p className="text-[11px] uppercase tracking-wide text-gray-400 dark:text-slate-500 mb-1.5">{group}</p>
                      <div className="space-y-1.5">
                        {items.map((f) => (
                          <div key={f.key} className="flex items-center justify-between gap-3">
                            <span className="text-sm text-gray-800 dark:text-slate-200">{f.label}</span>
                            <Segmented value={permissions.features[f.key]} options={levelOptions(f.levels)} onChange={(v) => setFeature(f.key, v)} />
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </Section>

              <Section icon={Briefcase} title="Brands" hint="Choose which of the account's brands they can open, and whether they can add new ones.">
                <div className="space-y-4">
                  {Object.keys(KIND_LABEL).map((kind) => {
                    const b = permissions.brands[kind];
                    const list = brands[kind] || [];
                    return (
                      <div key={kind} className="rounded-lg border border-gray-200 dark:border-slate-700 p-3">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                          <div>
                            <p className="text-sm font-medium text-gray-900 dark:text-slate-100">{KIND_LABEL[kind]} <span className="text-xs font-normal text-gray-400">({list.length})</span></p>
                            <p className="text-xs text-gray-500 dark:text-slate-400">{KIND_HINT[kind]}</p>
                          </div>
                          <div className="flex items-center gap-4">
                            <Segmented
                              value={b.see}
                              options={[{ value: 'all', label: 'See all' }, { value: 'selected', label: `Selected${b.see === 'selected' ? ` (${brandCounts[kind]})` : ''}` }]}
                              onChange={(v) => setBrand(kind, { see: v })}
                            />
                            <label className="flex items-center gap-2 text-xs text-gray-700 dark:text-slate-300">
                              <Toggle checked={b.add} onChange={(v) => setBrand(kind, { add: v })} label={`Can add ${KIND_LABEL[kind]}`} />
                              Can add
                            </label>
                          </div>
                        </div>
                        {b.see === 'selected' && (
                          <div className="mt-3">
                            <PickList
                              items={list}
                              selected={brandIds}
                              onToggle={toggleIn(setBrandIds)}
                              onSetAll={setAllIn(setBrandIds)}
                              empty={`No ${KIND_LABEL[kind].toLowerCase()} on this account yet.`}
                              renderLabel={(it) => `${it.name || it.url} — ${it.url}${it.userBrandUrl ? ` vs ${it.userBrandUrl}` : ''}`}
                            />
                            {b.add && <p className="mt-1.5 text-xs text-gray-500 dark:text-slate-400">Anything they add is shared with them automatically.</p>}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </Section>

              <Section icon={Globe} title="Data access" hint="Narrows the affiliate lists they see (Recruitment, Directory, and a brand's live affiliates).">
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center justify-between gap-3 mb-2">
                      <span className="text-sm text-gray-800 dark:text-slate-200">Verticals</span>
                      <Segmented
                        value={Array.isArray(permissions.verticals) ? 'some' : 'all'}
                        options={[{ value: 'all', label: 'All' }, { value: 'some', label: 'Only these' }]}
                        onChange={(v) => setPermissions((p) => ({ ...p, verticals: v === 'all' ? null : (p.verticals || [...(options.verticals || [])]) }))}
                      />
                    </div>
                    {Array.isArray(permissions.verticals) && (
                      <div className="flex flex-wrap gap-1.5">
                        {(options.verticals || []).map((v) => {
                          const on = permissions.verticals.includes(v);
                          return (
                            <button
                              key={v}
                              type="button"
                              onClick={() => setPermissions((p) => ({
                                ...p,
                                verticals: on ? p.verticals.filter((x) => x !== v) : [...p.verticals, v],
                              }))}
                              className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${on
                                ? 'bg-blue-600 border-blue-600 text-white'
                                : 'border-gray-300 dark:border-slate-600 text-gray-600 dark:text-slate-300 hover:border-blue-400'}`}
                            >
                              {v}
                            </button>
                          );
                        })}
                        {permissions.verticals.length === 0 && <span className="text-xs text-amber-600">Pick at least one, or choose All.</span>}
                      </div>
                    )}
                  </div>

                  <div>
                    <div className="flex items-center justify-between gap-3 mb-2">
                      <span className="text-sm text-gray-800 dark:text-slate-200">Countries</span>
                      <Segmented
                        value={Array.isArray(permissions.countries) ? 'some' : 'all'}
                        options={[{ value: 'all', label: 'All' }, { value: 'some', label: 'Only these' }]}
                        onChange={(v) => setPermissions((p) => ({ ...p, countries: v === 'all' ? null : (p.countries || []) }))}
                      />
                    </div>
                    {Array.isArray(permissions.countries) && (
                      <>
                        {permissions.countries.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mb-2">
                            {permissions.countries.map((c) => (
                              <Badge key={c} tone="blue">
                                {c}
                                <button type="button" aria-label={`Remove ${c}`} onClick={() => setPermissions((p) => ({ ...p, countries: p.countries.filter((x) => x !== c) }))}>×</button>
                              </Badge>
                            ))}
                          </div>
                        )}
                        <PickList
                          items={options.countries || []}
                          selected={new Set(permissions.countries)}
                          onToggle={(c) => setPermissions((p) => ({
                            ...p,
                            countries: p.countries.includes(c) ? p.countries.filter((x) => x !== c) : [...p.countries, c],
                          }))}
                          onSetAll={(items, value) => setPermissions((p) => {
                            const set = new Set(p.countries);
                            items.forEach((c) => (value ? set.add(c) : set.delete(c)));
                            return { ...p, countries: [...set] };
                          })}
                          empty="No countries available"
                          renderLabel={(c) => c}
                          height="max-h-40"
                        />
                        {permissions.countries.length === 0 && <p className="mt-1 text-xs text-amber-600">Pick at least one, or choose All.</p>}
                      </>
                    )}
                  </div>

                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <span className="flex items-center gap-2 text-sm text-gray-800 dark:text-slate-200"><Rows3 className="w-4 h-4 text-gray-400" />Rows visible per table</span>
                    <div className="flex items-center gap-2">
                      <Segmented
                        value={permissions.row_limit ? 'limit' : 'plan'}
                        options={[{ value: 'plan', label: options.rowLimitMax ? `Plan max (${options.rowLimitMax})` : 'Plan max (unlimited)' }, { value: 'limit', label: 'Limit to' }]}
                        onChange={(v) => setPermissions((p) => ({ ...p, row_limit: v === 'plan' ? null : (p.row_limit || Math.min(100, options.rowLimitMax || 100)) }))}
                      />
                      {permissions.row_limit && (
                        <input
                          type="number"
                          min={1}
                          max={options.rowLimitMax || undefined}
                          value={permissions.row_limit}
                          onChange={(e) => setPermissions((p) => ({ ...p, row_limit: Math.max(1, parseInt(e.target.value, 10) || 1) }))}
                          className="w-24 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-1 text-sm text-gray-900 dark:text-slate-100"
                        />
                      )}
                    </div>
                  </div>

                  <div className="flex items-center justify-between gap-3">
                    <span className="flex items-center gap-2 text-sm text-gray-800 dark:text-slate-200"><Download className="w-4 h-4 text-gray-400" />Can export CSV</span>
                    <Toggle checked={permissions.export} onChange={(v) => setPermissions((p) => ({ ...p, export: v }))} label="Can export CSV" />
                  </div>
                </div>
              </Section>

              {mode !== 'template' && (
                <Section icon={Mail} title="Email" hint="Each person connects their own mailboxes. You can also let them send from and read one of the account's mailboxes.">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-sm text-gray-800 dark:text-slate-200">Can connect their own mailboxes</span>
                      <Toggle checked={permissions.email.connect_own} onChange={(v) => setPermissions((p) => ({ ...p, email: { ...p.email, connect_own: v } }))} label="Can connect their own mailboxes" />
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-600 dark:text-slate-300 mb-1.5">Share account mailboxes</p>
                      {(options.mailboxes || []).length === 0 ? (
                        <p className="text-xs text-gray-500 dark:text-slate-400">No mailboxes connected on this account yet (Messages → Email Settings).</p>
                      ) : (
                        <div className="space-y-1">
                          {options.mailboxes.map((m) => (
                            <label key={m.id} className="flex items-center gap-2 text-sm text-gray-700 dark:text-slate-300">
                              <input type="checkbox" checked={mailboxIds.has(m.id)} onChange={() => toggleIn(setMailboxIds)(m.id)} />
                              <span>{m.email}</span>
                              {m.display_name && <span className="text-xs text-gray-400">({m.display_name})</span>}
                              {!m.is_verified && <Badge tone="amber">not verified</Badge>}
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </Section>
              )}
            </>
          )}
        </div>

        {/* footer */}
        <div className="px-5 py-3 border-t border-gray-200 dark:border-slate-700">
          {saveAsName !== null && (
            <div className="flex items-center gap-2 mb-3">
              <input autoFocus className={inputClass} value={saveAsName} onChange={(e) => setSaveAsName(e.target.value)} placeholder="Template name" maxLength={80}
                onKeyDown={(e) => { if (e.key === 'Enter') saveAsTemplate(); }} />
              <Button onClick={saveAsTemplate} disabled={saving || !saveAsName.trim()}>Save template</Button>
              <Button variant="ghost" onClick={() => setSaveAsName(null)}>Cancel</Button>
            </div>
          )}
          <div className="flex items-center justify-between gap-2">
            <div>
              {!isCoAdmin && mode !== 'template' && permissions && saveAsName === null && (
                <Button variant="ghost" onClick={() => setSaveAsName('')}>
                  <BookmarkPlus className="w-4 h-4" /> Save as template
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="secondary" onClick={onClose}>{isCoAdmin ? 'Close' : 'Cancel'}</Button>
              {!isCoAdmin && (
                <Button onClick={save} disabled={saving || loading || !permissions}>
                  {saving ? 'Saving…' : mode === 'bulk' ? `Apply to ${memberIds.length}` : 'Save'}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthorityDrawer;
